import React, { useMemo } from 'react';
import { useDesignStore, type DesignSide } from '../../store/designStore';
import { crossOriginForUrl } from '../../lib/crossOriginImage';
import { ScrollStrip } from './ScrollStrip';

export function SideSelector() {
  const { sides, activeSideKey, setActiveSide } = useDesignStore();

  // Group placeholders by their underlying photo so a photo with multiple
  // placeholders (e.g. a "Logo" box and a "Text" box) gets ONE tab instead of
  // one tab per placeholder — switching between placeholders happens via the
  // compact picker below, not via separate top-level tabs.
  const groups = useMemo(() => {
    const map = new Map<string, DesignSide[]>();
    for (const s of sides) {
      const group = map.get(s.photoId);
      if (group) group.push(s);
      else map.set(s.photoId, [s]);
    }
    return Array.from(map.values());
  }, [sides]);

  const activeSide = sides.find(s => s.sideKey === activeSideKey);
  const activeGroup = activeSide ? groups.find(g => g[0].photoId === activeSide.photoId) : undefined;

  if (groups.length <= 1 && (activeGroup?.length ?? 0) <= 1) return null;

  // const photoTabs = groups.length > 1 && (
  //   <div style={{ display: 'flex', gap: 10, background: 'var(--surface)', padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
  //     {groups.map(group => {
  //       const isActiveGroup = activeSide?.photoId === group[0].photoId;
  //       const targetKey = isActiveGroup ? activeSideKey : group[0].sideKey;
  //       return (
  //         <button
  //           key={group[0].photoId}
  //           onClick={() => setActiveSide(targetKey)}
  //           style={{
  //             display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  //             width: 64, border: 'none', background: 'none', cursor: 'pointer', padding: 0,
  //           }}
  //         >
  //           <div style={{
  //             width: 56, height: 56, borderRadius: 6, overflow: 'hidden',
  //             border: isActiveGroup ? '2px solid var(--primary)' : '1px solid var(--border)',
  //             boxShadow: isActiveGroup ? '0 0 0 2px color-mix(in srgb, var(--primary) 25%, transparent)' : 'none',
  //             background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  //           }}>
  //             <img
  //               src={group[0].imagePath}
  //               alt=""
  //               crossOrigin={crossOriginForUrl(group[0].imagePath)}
  //               style={{ width: '100%', height: '100%', objectFit: 'contain' }}
  //             />
  //           </div>
  //           <span style={{
  //             fontSize: 12, fontWeight: 500, textAlign: 'center', lineHeight: 1.2,
  //             color: isActiveGroup ? 'var(--primary)' : 'var(--text-muted)',
  //             overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
  //           }}>
  //             {group[0].photoLabel}
  //           </span>
  //         </button>
  //       );
  //     })}
  //   </div>
  // );
  const photoTabs = groups.length > 1 && (
    <div style={{ background: 'var(--surface)', padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
      <ScrollStrip>
        {groups.map(group => {
          const isActiveGroup = activeSide?.photoId === group[0].photoId;
          const targetKey = isActiveGroup ? activeSideKey : group[0].sideKey;
          return (
            <button
              key={group[0].photoId}
              onClick={() => setActiveSide(targetKey)}
              title={group[0].photoLabel}
              aria-label={group[0].photoLabel}
              aria-pressed={isActiveGroup}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                width: 82, flexShrink: 0, alignSelf: 'flex-start',
                border: 'none', background: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: 6, overflow: 'hidden',
                border: isActiveGroup ? '2px solid var(--primary)' : '1px solid var(--border)',
                boxShadow: isActiveGroup ? '0 0 0 2px color-mix(in srgb, var(--primary) 25%, transparent)' : 'none',
                background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img
                  src={group[0].imagePath}
                  alt=""
                  crossOrigin={crossOriginForUrl(group[0].imagePath)}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <span style={{
                fontSize: 12, textAlign: 'center', lineHeight: 1.2, minHeight: '2.4em', maxWidth: '100%',
                fontWeight: isActiveGroup ? 700 : 500,
                color: isActiveGroup ? '#0f172a' : '#475569',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', overflowWrap: 'anywhere',
              }}>
                {group[0].photoLabel}
              </span>
            </button>
          );
        })}
      </ScrollStrip>
    </div>
  );
  // Compact picker for switching between placeholders within the active photo —
  // only shown when that photo actually has more than one placeholder.
  const placeholderPicker = activeGroup && activeGroup.length > 1 && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Editing:</span>
      {activeGroup.map(placeholder => (
        <button
          key={placeholder.sideKey}
          onClick={() => setActiveSide(placeholder.sideKey)}
          style={{
            padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            border: activeSideKey === placeholder.sideKey ? '1px solid var(--primary)' : '1px solid var(--border)',
            background: activeSideKey === placeholder.sideKey ? 'var(--primary)' : 'transparent',
            color: activeSideKey === placeholder.sideKey ? '#fff' : 'var(--text-muted)',
          }}
        >
          {placeholder.placeholderLabel}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      {photoTabs}
      {placeholderPicker}
    </div>
  );
}
